/**
 * PickupDeliveryLookupDialog — Batch 3 · Fase 6 (Feb 2026)
 *
 * Menselijke lookup voor Pickup/Delivery afspraken. Zoek op naam, telefoon,
 * e-mail of W-nummer — technische pickup_id is niet nodig. Elk resultaat
 * toont een badge "pickup" of "delivery" zodat de medewerker weet welk
 * type afspraak dit is.
 *
 * Props:
 *  - open: bool
 *  - onOpenChange(open)
 *  - onSelect({id, result_type}) — callback: open het detail
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, formatApiError } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Search, Truck, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  pickup: { label: "Pickup", icon: Truck, cls: "bg-amber-100 text-amber-800" },
  delivery: { label: "Delivery", icon: Package, cls: "bg-emerald-100 text-emerald-800" },
};

const FIELD_LABEL = {
  customer_name: "naam",
  company_name: "bedrijf",
  email: "e-mail",
  phone: "telefoon",
  pickup_city: "pickup-plaats",
  delivery_city: "delivery-plaats",
  tracking_number: "W-nummer",
  w_number: "W-nummer",
};

export default function PickupDeliveryLookupDialog({ open, onOpenChange, onSelect }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const searchTimer = useRef(null);

  const runSearch = useMemo(() => async (term) => {
    const trimmed = (term || "").trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.get("/admin/pickup-delivery/lookup", {
        params: { q: trimmed, limit: 25 },
      });
      setResults(r.data?.results || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(q), 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [q, runSearch]);

  useEffect(() => {
    if (!open) {
      // Fase 6 stale-reset: bij openen opnieuw leeg beginnen.
      setQ("");
      setResults([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="pickup-delivery-lookup-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#0A192F]">
            Pickup / Delivery opzoeken
          </DialogTitle>
          <DialogDescription>
            Zoek op klantnaam, telefoon, e-mail of W-nummer. De technische ID is niet nodig.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="pickup-delivery-lookup-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Bijv. Yasira, 06-12345678, W523515792943, of e-mailadres"
            className="rounded-none pl-9"
          />
        </div>

        <div className="mt-2 min-h-[180px] max-h-[420px] overflow-y-auto">
          {busy && (
            <div className="flex items-center gap-2 text-xs text-gray-600 py-2" data-testid="pickup-delivery-lookup-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Zoeken…
            </div>
          )}
          {!busy && q.length >= 2 && results.length === 0 && (
            <div className="text-xs text-gray-500 py-6 text-center" data-testid="pickup-delivery-lookup-empty">
              Geen afspraken gevonden voor &quot;{q}&quot;.
            </div>
          )}
          {results.map((hit) => {
            const meta = TYPE_LABEL[hit.result_type] || TYPE_LABEL.pickup;
            const Icon = meta.icon;
            const matchedLabel = FIELD_LABEL[hit.matched_field] || hit.matched_field;
            return (
              <button
                key={hit.id}
                type="button"
                onClick={() => { onSelect?.(hit); onOpenChange?.(false); }}
                data-testid={`pickup-delivery-lookup-result-${hit.id}`}
                className="w-full text-left bg-white border border-gray-200 hover:border-[#0055FF] hover:bg-[#F8F9FA] p-3 mb-2 flex items-start gap-3 transition-colors"
              >
                <div className={`inline-flex items-center justify-center w-9 h-9 shrink-0 ${meta.cls}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 ${meta.cls}`} data-testid={`pickup-delivery-lookup-result-${hit.id}-type`}>
                      {meta.label}
                    </span>
                    <span className="font-medium text-sm text-[#0A192F] truncate">
                      {hit.customer_name || hit.company_name || "Onbekend"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 truncate">
                    {hit.pickup_date && <span>{hit.pickup_date} </span>}
                    {hit.pickup_city && <span>· {hit.pickup_city} </span>}
                    {hit.tracking_number && <span className="font-mono">· {hit.tracking_number}</span>}
                  </div>
                  {matchedLabel && (
                    <div className="text-[10px] text-[#0055FF] uppercase tracking-wide mt-0.5">
                      Match op {matchedLabel}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
